import * as React from 'react';
import './StockItemForm.scss';
import { TK } from '../../../store/translations/translationKeys';
import { Spinner, Form, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import Delete from '@material-ui/icons/Delete';
import { TextInputField, NumberInputField, AutocompleteTextField } from '../fields';
import { StockData, FeatureEnum } from '../../../models';
import ChangeLogInfo from '../../ChangeLogInfo';
import { ChangeLog } from '../../../models';
import { useMemo } from 'react';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { updateGeneralSettings } from '../../../store/settings/actions';
import { useDispatch } from 'react-redux';
import AppButton from '../../AppButton';

export interface StockItemFormStateProps {
    data: StockData,
    hasChanged: boolean,
    isSubmitting?: boolean,
    type: 'edit' | 'create',
    canEdit: boolean,
    changeLog?: ChangeLog[],
}

export interface StockItemFormDispatchProps {
    onChange: (data: StockData) => void,
    onLoad?: () => void,
    onSave: (data: StockData) => void,
    onCancel?: () => void,
    onDelete?: () => void,
}

export type StockItemFormProps = StockItemFormStateProps & StockItemFormDispatchProps;

const StockItemForm: React.FC<StockItemFormProps> = ({
    type,
    data,
    hasChanged,
    isSubmitting,
    onChange,
    onLoad,
    onSave,
    onCancel,
    onDelete,
    canEdit,
    changeLog,
}) => {

    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const {stockCategories} = generalSettings;

    const [deleteModal, setDeleteModal] = React.useState(false);

    // eslint-disable-next-line
    React.useEffect(() => { onLoad && onLoad(); }, []);

    const isEditing = type === 'edit';

    const id = useMemo(() => isEditing ? data.id : 'new', [isEditing, data.id]);

    const handleSave = () => {
        onSave({
            ...data,
            name: data.name?.trim(),
        });
    };

    return (
        <>
            <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
                <TextInputField
                    id={`stock-name-${id}`}
                    label={t(TK.name)}
                    value={data.name || ''}
                    disabled={!canEdit}
                    onChange={name => onChange({...data, name})}
                />

                <AutocompleteTextField
                    id={`stock-category-${id}`}
                    label={t(TK.category)}
                    options={stockCategories || []}
                    value={data.category || ''}
                    disabled={!canEdit}
                    onValueChanged={category => onChange({...data, category})}
                    onAddNewOption={newCategory => dispatch(updateGeneralSettings({
                        ...generalSettings,
                        stockCategories: [...(generalSettings.stockCategories || []), newCategory],
                    }))}
                />

                <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
                    <NumberInputField
                        id={`stock-default-price-${id}`}
                        style={{flexGrow: 1}}
                        label={t(TK.defaultPrice)}
                        prepend="€"
                        value={data.defaultPrice ?? 0}
                        disabled={!canEdit}
                        onChange={defaultPrice => onChange({
                            ...data,
                            defaultPrice: defaultPrice ?? 0
                        })}
                    />
                    <NumberInputField
                        id={`stock-count-${id}`}
                        style={{flexGrow: 1}}
                        label={t(TK.count)}
                        value={data.amount ?? 0}
                        disabled={type !== 'create'}
                        onChange={amount => onChange({
                            ...data,
                            amount: amount ?? 0
                        })}
                    />
                </div>
            </Form>

            <div className="buttonsContainer" >
                { isEditing && canEdit && onDelete && <AppButton id={`stock-delete-button-${id}`} outline color="danger" onClick={() => setDeleteModal(true)}><Delete /></AppButton>}
                { onCancel && (<AppButton id={`stock-cancel-button-${id}`} onClick={onCancel}>{t(TK.cancel)}</AppButton>) }
                <AppButton
                    id={`stock-save-button-${id}`}
                    color="success"
                    disabled={isSubmitting || !hasChanged || !data.name}
                    onClick={handleSave}
                >
                    {isSubmitting && <Spinner size="sm" color="secondary"/>}
                    {t(isEditing ? TK.save : TK.create)}
                </AppButton>
                { !!changeLog?.length && <ChangeLogInfo changeLog={changeLog}/> }
            </div>

            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>{t(TK.youRAboutToDelete)}</ModalBody>
                <ModalFooter>
                    <AppButton id={`stock-confirm-delete-button-${id}`} color="danger" onClick={() => { onDelete && onDelete(); setDeleteModal(false); }}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={`stock-abort-delete-button-${id}`} color="secondary" onClick={() => setDeleteModal(false)}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default StockItemForm;
